import React, { useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Stack,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Grid,
    Button,
    Menu,
} from '@mui/material';
import ApexCharts from 'react-apexcharts';
import moment from 'moment';
import {
    MonetizationOn,
    Group,
    Cancel,
    GridOn,
    PictureAsPdf,
    ArrowDropDown,
    TrendingUp,
    ShowChart,
    BarChart,
    PieChart
} from '@mui/icons-material';

const filterOptions = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'weekly' },
    { label: 'This Month', value: 'monthly' },
    { label: 'This Quarter', value: 'quarterly' },
    { label: 'This Year', value: 'yearly' },
    { label: 'Last 3 Years', value: '3years' },
    { label: 'Last 5 Years', value: '5years' },
    { label: 'Last 10 Years', value: '10years' },
    { label: 'Lifetime', value: 'lifetime' }
];

const FinancialReportsSummary = ({
    reportsData = [],
    filterType,
    onFilterChange,
    onExport
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // Process data
    const activeMembers = reportsData.filter((u) => u.status?.toLowerCase() === 'active').length;
    const cancelledMembers = reportsData.filter((u) => u.status?.toLowerCase() !== 'active').length;
    const netRevenue = reportsData.reduce((acc, curr) => acc + (parseFloat(curr?.totalAmount) || 0), 0);
    const avgRevenuePerUser = activeMembers > 0 ? (netRevenue / activeMembers).toFixed(2) : 0;

    // Data aggregation by date
    const activeUsersByDate = {};
    const cancelledUsersByDate = {};
    const revenueByDate = {};
    const totalUsersByDate = {};
    const cumulativeRevenue = [];
    let cumulativeTotal = 0;

    reportsData.forEach((user) => {
        const date = user.subscriptionStartDate ? moment(user.subscriptionStartDate).format('YYYY-MM-DD') : null;
        if (!date) return;

        const amount = parseFloat(user?.totalAmount) || 0;
        if (user.status?.toLowerCase() === 'active') {
            activeUsersByDate[date] = (activeUsersByDate[date] || 0) + 1;
        } else {
            cancelledUsersByDate[date] = (cancelledUsersByDate[date] || 0) + 1;
        }

        revenueByDate[date] = (revenueByDate[date] || 0) + amount;
        totalUsersByDate[date] = (totalUsersByDate[date] || 0) + 1;
    });

    const sortedDates = Array.from(new Set([
        ...Object.keys(activeUsersByDate),
        ...Object.keys(cancelledUsersByDate),
        ...Object.keys(revenueByDate),
        ...Object.keys(totalUsersByDate)
    ])).sort((a, b) => new Date(a) - new Date(b));

    sortedDates.forEach((date) => {
        cumulativeTotal += revenueByDate[date] || 0;
        cumulativeRevenue.push(cumulativeTotal);
    });

    // Calculate growth rates
    // const calculateGrowthRate = (values) => {
    //     if (values.length < 2) return 0;
    //     const first = values[0] || 1;
    //     const last = values[values.length - 1] || 1;
    //     return ((last - first) / first) * 100;
    // };

    // const userGrowthRate = calculateGrowthRate(
    //     sortedDates.map(date => totalUsersByDate[date] || 0)
    // ).toFixed(1);

    // const revenueGrowthRate = calculateGrowthRate(
    //     sortedDates.map(date => revenueByDate[date] || 0)
    // ).toFixed(1);

    // Chart series
    const chartSeries = {
        users: [
            { name: 'Active Users', data: sortedDates.map(date => activeUsersByDate[date] || 0) },
            { name: 'Cancelled Users', data: sortedDates.map(date => cancelledUsersByDate[date] || 0) }
        ],
        revenue: [
            { name: 'Daily Revenue', data: sortedDates.map(date => revenueByDate[date] || 0) }
        ],
        totalUsers: [
            { name: 'Total Users', data: sortedDates.map(date => totalUsersByDate[date] || 0) }
        ],
        cumulativeRevenue: [
            { name: 'Cumulative Revenue', data: cumulativeRevenue }
        ],
        activeOnly: [
            { name: 'Active Users', data: sortedDates.map(date => activeUsersByDate[date] || 0) }
        ],
        revenueDistribution: [
            { name: 'Revenue by Day', data: sortedDates.map(date => revenueByDate[date] || 0) }
        ]
    };

    // Enhanced chart options
    const chartOptions = (type, title) => {
        const baseOptions = {
            chart: {
                type: type === 'bar' ? 'bar' : 'area',
                toolbar: { show: true },
                zoom: { enabled: true },
                animations: { enabled: true }
            },
            stroke: {
                curve: 'smooth',
                width: type === 'bar' ? 0 : 2
            },
            dataLabels: { enabled: false },
            xaxis: {
                categories: sortedDates,
                labels: {
                    rotate: -45,
                    style: { colors: '#6b7280' }
                },
                axisBorder: { show: false }
            },
            yaxis: {
                labels: {
                    formatter: (val) => type === 'revenue' ? `$${val.toLocaleString()}` : val.toLocaleString(),
                    style: { colors: '#6b7280' }
                },
                axisBorder: { show: false }
            },
            grid: {
                borderColor: '#f3f4f6',
                strokeDashArray: 4
            },
            tooltip: {
                y: {
                    formatter: (val) => type === 'revenue' ? `$${val.toLocaleString()}` : val.toLocaleString()
                },
                theme: 'dark'
            },
            title: {
                text: title,
                align: 'left',
                style: {
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#111827'
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                markers: { radius: 12 }
            }
        };

        switch (type) {
            case 'users':
                return {
                    ...baseOptions,
                    colors: ['#10b981', '#ef4444'],
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            opacityFrom: 0.7,
                            opacityTo: 0.3,
                            stops: [0, 90, 100]
                        }
                    }
                };
            case 'revenue':
                return {
                    ...baseOptions,
                    colors: ['#3b82f6'],
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            opacityFrom: 0.7,
                            opacityTo: 0.3,
                            stops: [0, 90, 100]
                        }
                    }
                };
            case 'cumulative':
                return {
                    ...baseOptions,
                    colors: ['#8b5cf6'],
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shadeIntensity: 1,
                            opacityFrom: 0.7,
                            opacityTo: 0.3,
                            stops: [0, 90, 100]
                        }
                    }
                };
            case 'bar':
                return {
                    ...baseOptions,
                    colors: ['#f59e0b'],
                    plotOptions: {
                        bar: {
                            borderRadius: 4,
                            horizontal: false,
                            columnWidth: '60%'
                        }
                    }
                };
            case 'pie':
                return {
                    ...baseOptions,
                    chart: { type: 'pie' },
                    labels: ['Active Users', 'Cancelled Users'],
                    colors: ['#10b981', '#ef4444'],
                    legend: {
                        position: 'bottom'
                    },
                    responsive: [{
                        breakpoint: 480,
                        options: {
                            chart: { width: 200 },
                            legend: { position: 'bottom' }
                        }
                    }]
                };
            default:
                return baseOptions;
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleExport = (type) => {
        onExport(type);
        handleClose();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header with title and controls */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                    Financial Performance Dashboard
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Time Period</InputLabel>
                        <Select
                            value={filterType}
                            label="Time Period"
                            onChange={(e) => onFilterChange(e.target.value)}
                        >
                            {filterOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        onClick={handleClick}
                        sx={{
                            backgroundColor: '#faaa2f',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: '#faaa2f',
                            },
                        }}
                        endIcon={<ArrowDropDown />}
                    >
                        Export Report
                    </Button>

                    <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                        <MenuItem onClick={() => handleExport('excel')}>
                            <GridOn sx={{ mr: 1, color: '#10b981' }} /> Export to Excel
                        </MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>
                            <PictureAsPdf sx={{ mr: 1, color: '#ef4444' }} /> Export to PDF
                        </MenuItem>
                    </Menu>
                </Stack>
            </Stack>

            {/* Summary Metrics */}
            <Grid container spacing={3} mb={4}>
                {/* Net Revenue Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2, height: '100%', borderLeft: '4px solid #3b82f6' }}>
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                            <MonetizationOn sx={{ fontSize: 40, color: '#3b82f6' }} />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Net Revenue
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    ${netRevenue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* Active Members Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2, height: '100%', borderLeft: '4px solid #10b981' }}>
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                            <Group sx={{ fontSize: 40, color: '#10b981' }} />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Active Members
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    {activeMembers.toLocaleString()}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* Cancelled Members Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2, height: '100%', borderLeft: '4px solid #ef4444' }}>
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                            <Cancel sx={{ fontSize: 40, color: '#ef4444' }} />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Cancelled Members
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    {cancelledMembers.toLocaleString()}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* ARPU Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2, height: '100%', borderLeft: '4px solid #f59e0b' }}>
                        <Stack direction="row" alignItems="flex-start" spacing={2}>
                            <TrendingUp sx={{ fontSize: 40, color: '#f59e0b' }} />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Avg Revenue Per User
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                    ${avgRevenuePerUser}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Charts Section */}
            <Grid container spacing={4}>
                {/* User Growth Chart */}
                <Grid item xs={12} lg={8}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <ShowChart color="primary" />
                            <Typography variant="h6" fontWeight="bold">User Growth Over Time</Typography>
                        </Stack>
                        <ApexCharts
                            options={chartOptions('users', 'Active vs Cancelled Users')}
                            series={chartSeries.users}
                            type="area"
                            height={350}
                        />
                    </Card>
                </Grid>

                {/* User Distribution Pie Chart */}
                <Grid item xs={12} lg={4}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <PieChart color="primary" />
                            <Typography variant="h6" fontWeight="bold">User Distribution</Typography>
                        </Stack>
                        <ApexCharts
                            options={chartOptions('pie', '')}
                            series={[activeMembers, cancelledMembers]}
                            type="pie"
                            height={350}
                        />
                    </Card>
                </Grid>

                {/* Revenue Charts */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <MonetizationOn color="primary" />
                            <Typography variant="h6" fontWeight="bold">Daily Revenue</Typography>
                        </Stack>
                        <ApexCharts
                            options={chartOptions('revenue', 'Daily Revenue')}
                            series={chartSeries.revenue}
                            type="area"
                            height={300}
                        />
                    </Card>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <TrendingUp color="primary" />
                            <Typography variant="h6" fontWeight="bold">Cumulative Revenue</Typography>
                        </Stack>
                        <ApexCharts
                            options={chartOptions('cumulative', 'Cumulative Revenue Over Time')}
                            series={chartSeries.cumulativeRevenue}
                            type="area"
                            height={300}
                        />
                    </Card>
                </Grid>

                {/* Additional Metrics */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <BarChart color="primary" />
                            <Typography variant="h6" fontWeight="bold">Revenue Distribution by Day</Typography>
                        </Stack>
                        <ApexCharts
                            options={chartOptions('bar', '')}
                            series={chartSeries.revenueDistribution}
                            type="bar"
                            height={300}
                        />
                    </Card>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <Group color="primary" />
                            <Typography variant="h6" fontWeight="bold">Active Users Trend</Typography>
                        </Stack>
                        <ApexCharts
                            options={chartOptions('users', 'Active Users Only')}
                            series={chartSeries.activeOnly}
                            type="area"
                            height={300}
                        />
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default FinancialReportsSummary;